import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { useTranslation } from 'react-i18next';

import { IReportDataObject, IPaginatedReportData } from '../../../entities/reports/IReportData';
import { IData as IClientDetail, ISaveData } from "../../../store/client/clientItem";
import { IData as IClientListData } from "../../../store/client/clientList";
import { FetchState } from '../../../entities/FetchState';
import { INavigations, mapNavigationsToProps, ClientNavigation, ClientNavigationPage } from '../../organisms/client/ClientNavigation';
import { PageHeader } from '../../organisms/PageHeader';
import { ClientEdit } from '../../organisms/client/ClientEdit';
import { SaveButton } from '../../atoms/buttons/SaveButton';
import ErrorMessage from '../../organisms/ErrorMessage';
import { SavingMessage } from '../../organisms/SavingMessage';

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch {}

// Properties from State 
interface IStateProps { 
    saver: IReportDataObject<IClientDetail>
    clientList: IPaginatedReportData<IClientListData>
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
    saver: state.client.clientItem.SaveItem,
    clientList: state.client.clientList.ClientList
});

// Outgoing functions to Dispatch
interface IDispatchProps { 
    navigations: INavigations;
    onSave: (streamId: string, details: ISaveData) => void;
    onSearchClients: (search: string) => void;
    onClickClientDetails: (streamId: string) => void;
}
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    navigations: mapNavigationsToProps(dispatch),
    onSave: (streamId: string, details: ISaveData) => dispatch(RootActionCreators.Client.clientItem.SaveData(streamId, details)),
    onSearchClients: (search: string) => dispatch(RootActionCreators.Client.clientList.FetchData(search)),
    onClickClientDetails: (streamId: string) => dispatch(RootActionCreators.Navigate.ToClientDetail(streamId))
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps;
export const ClientNewPage = (props: AllProps) => {
    const { t: tClient } = useTranslation('client');
    const { t: tCommon } = useTranslation('common');

    // Generate a random UUID for the new client
    const [streamId] = React.useState<string>(crypto.randomUUID());
    const [saveData, setSaveData] = React.useState<ISaveData | null>(null);
    const [isValid, setIsValid] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState<string>('');

    // Debounced search effect
    React.useEffect(() => {
        if (searchTerm.length >= 2) {
            const timer = setTimeout(() => {
                props.onSearchClients(searchTerm);
            }, 500); // Wait 500ms after user stops typing
            
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [searchTerm]);

    // Update search term when firstName, lastName, or burgerServiceNumber changes
    const handleFormChange = (data: ISaveData, valid: boolean) => {
        setSaveData(data);
        setIsValid(valid);
        
        // Combine firstName, lastName, and burgerServiceNumber for search
        const nameTerm = `${data.firstName || ''} ${data.lastName || ''}`.trim();
        const newSearchTerm = data.burgerServiceNumber 
            ? `${nameTerm} ${data.burgerServiceNumber}`.trim() 
            : nameTerm;
        
        if (newSearchTerm !== searchTerm) {
            setSearchTerm(newSearchTerm);
        }
    };

    // Check for exact rijksregisternummer match
    const exactMatch = props.clientList.state === FetchState.Success && saveData?.burgerServiceNumber
        ? props.clientList.data.find(c => 
            c.burgerServiceNumber && 
            c.burgerServiceNumber.replace(/[\s.-]/g, '').toLowerCase() === 
            saveData.burgerServiceNumber.replace(/[\s.-]/g, '').toLowerCase()
        )
        : undefined;

    // Get top 5 suggestions from search results (excluding exact match)
    // Show suggestions if:
    // 1. Client list is successfully loaded AND
    // 2. Either search term has 2+ characters OR there's already data in the list (for Storybook)
    const suggestions = props.clientList.state === FetchState.Success && 
                        (searchTerm.length >= 2 || props.clientList.data.length > 0)
        ? props.clientList.data.filter(c => !exactMatch || c.streamId !== exactMatch.streamId).slice(0, 5)
        : [];

    // Empty initial data for new client
    const emptyInitialData: Partial<ISaveData> = {
        firstName: '',
        lastName: '',
        burgerServiceNumber: '',
        dateOfBirth: null,
        gender: null,
        officialStreet: '',
        residencyStreet: null,
        officialStreetNumber: '',
        residencyStreetNumber: null,
        homePhone: null,
        workPhone: null,
        mobilePhone: null,
        email: null,
        contactPerson: null,
        remarks: null,
        disabilityStatus: 'None',
        languageStreamId: null,
        residencyCityStreamId: null,
        officialCityStreamId: '',
        activityTypeStreamId: null,
        disabilityId: [],
        problemId: []
    };

    return (
        <ClientNavigation navigations={props.navigations} selected={ClientNavigationPage.New}>
            <PageHeader
                title={tClient('newTitle')}
                subTitle={tClient('newClientPageSubtitle')}
                explainText={tClient('newExplanation')}
            />

            {exactMatch && (
                <div style={{ 
                    padding: '16px', 
                    marginBottom: '16px', 
                    backgroundColor: '#f8d7da', 
                    border: '1px solid #dc3545',
                    borderRadius: '4px',
                    color: '#721c24'
                }}>
                    <strong>{tClient('exactMatchWarning')}</strong>
                    <a 
                        href="#" 
                        onClick={(e) => {
                            e.preventDefault();
                            props.onClickClientDetails(exactMatch.streamId);
                        }}
                        style={{ color: '#721c24', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        {tClient('exactMatchLink', { 
                            fullName: exactMatch.fullName, 
                            city: exactMatch.residencyCityName, 
                            birthYear: exactMatch.birthYear 
                        })}
                    </a>
                </div>
            )}

            {suggestions.length > 0 && (
                <div style={{ 
                    padding: '16px', 
                    marginBottom: '16px', 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffc107',
                    borderRadius: '4px'
                }}>
                    <strong>{tClient('suggestionsTitle')}</strong>
                    {suggestions.map((client, index) => (
                        <span key={client.streamId}>
                            <a 
                                href="#" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    props.onClickClientDetails(client.streamId);
                                }}
                                style={{ color: '#0066cc', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                                {tClient('exactMatchLink', { 
                                    fullName: client.fullName, 
                                    city: client.residencyCityName, 
                                    birthYear: client.birthYear 
                                })}
                            </a>
                            {index < suggestions.length - 1 ? ', ' : ''}
                        </span>
                    ))}
                </div>
            )}

            <ClientEdit
                streamId={streamId}
                initialData={emptyInitialData}
                loading={false}
                onChange={handleFormChange}
            />

            <SaveButton
                disabled={!isValid || !!exactMatch}
                onClick={() => saveData && props.onSave(streamId, saveData)}
            />

            {props.saver.state === FetchState.Busy && <SavingMessage entity={tClient('client')} />}
            {props.saver.state === FetchState.Error && <ErrorMessage
                title={tCommon('savingErrorTitle')}
                message={tCommon('savingErrorXMessage', {x: props.saver.errorMessage})}
                canClose
                options={[]}
                errorDetails={JSON.stringify(saveData)}
            />}
        </ClientNavigation>
    );
};

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(ClientNewPage);

